import { inject, Injectable, signal } from '@angular/core';
import { ApiClientConstant, ParseKeys, Table } from '@cureskin/api-client';
import { Experiment, User } from '@cureskin/api-client/src/server-api';
import { ConnectionService } from '@services/connection-service';
import { LocalStorageService } from '@services/local-storage-service';
import { AppConfig } from 'client/app/app.config';
import { extractRegimenProducts } from 'client/helper/regimen-helper';
import { UserService } from '@services/user/user.service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { Regimen, AllocatedRegimen } from '../../app/app-constant-types';
import { Store } from '@ngrx/store';
import { selectRegimens, selectRegimensFetched } from '@store/selectors/app.selectors';
import { RegimenStateUpdate } from '@store/actions/app.actions';

const VARIANT_ID = {
  variant_999: 'variant_999',
  variant_1499: 'variant_1499',
  variant_1999: 'variant_1999',
  variant_4999: 'variant_4999',
};

const REGIMEN_IMG_BACKGROUND_CLASS = {
  variant_999: 'tw-bg-teal-100',
  variant_1499: 'tw-bg-teal-100',
  variant_1999: 'tw-bg-orange-100',
  variant_4999: 'tw-bg-blue-100',
};

export type PlanName = 'advance' | 'pro' | 'assist' | 'introductory';
export type VariantId = 'variant_999' | 'variant_1499' | 'variant_1999' | 'variant_2999' | 'variant_4999';

export const selectedPlanAndVariantIdMap: Record<PlanName, VariantId> = {
  'advance': ApiClientConstant.Regimen.SelectedVariantId.variant_1499,
  'pro': ApiClientConstant.Regimen.SelectedVariantId.variant_1999,
  'assist': ApiClientConstant.Regimen.SelectedVariantId.variant_4999,
  'introductory': ApiClientConstant.Regimen.SelectedVariantId.variant_999,
};

export const planName = {
  advance: 'advance',
  pro: 'pro',
  assist: 'assist',
  introductory: 'introductory',
} as const;

export const REGIMEN_FETCH_INCLUDE: ParseKeys<Table.Regimen>[] = [
  'morning.product' as ParseKeys<Table.Regimen>,
  'night.product' as ParseKeys<Table.Regimen>,
  'regimenTag' as ParseKeys<Table.Regimen>,
  'variants.products' as ParseKeys<Table.Regimen>,
  'variants.morning.product' as ParseKeys<Table.Regimen>,
  'variants.night.product' as ParseKeys<Table.Regimen>,
  'variants.morning.product.heroIngredients' as ParseKeys<Table.Regimen>,
  'variants.night.product.heroIngredients' as ParseKeys<Table.Regimen>,
  'morning.product.heroIngredients' as ParseKeys<Table.Regimen>,
  'night.product.heroIngredients' as ParseKeys<Table.Regimen>,
  'variants.morning.product.productHighlights' as ParseKeys<Table.Regimen>,
  'variants.night.product.productHighlights' as ParseKeys<Table.Regimen>,
  'morning.product.productHighlights' as ParseKeys<Table.Regimen>,
  'night.product.productHighlights' as ParseKeys<Table.Regimen>,
];

type LinearGradient = {
  color1: string;
  color2: string;
};

@Injectable({ providedIn: 'root' })
export class RegimenService {
  private readonly _variantImagesReady = signal(false);
  public readonly variantImagesReady = this._variantImagesReady.asReadonly();

  private readonly _isAnyRegimenPreparationOngoing = signal(false);
  public readonly isAnyRegimenPreparationOngoing = this._isAnyRegimenPreparationOngoing.asReadonly();

  /** Global user regimens, backed by NgRx store. Updated whenever regimens are fetched via ConnectionService.fetchRegimens. */
  public readonly regimens = this.store.selectSignal(selectRegimens);
  private readonly regimensFetched = this.store.selectSignal(selectRegimensFetched);

  private readonly appBridgeService = inject(AppWebBridgeService);

  constructor(
    private readonly localStorageService: LocalStorageService,
    private readonly connectionService: ConnectionService,
    private readonly appConfig: AppConfig,
    private readonly userService: UserService,
    private readonly store: Store,
  ) {}
  variantId: string = 'variant_999';
  backgroundColor: string = 'tw-bg-teal-100';
  linearGradient: LinearGradient = {
    color1: '#A2DBD0',
    color2: '#14796D',
  };

  /**
   * Returns the regimen class to display when the user has allocated regimens.
   * When both face and hair regimens are allocated, face takes priority; otherwise returns the single allocated class.
   * @returns The regimen class to display (e.g. FACE, HAIR), or null if no regimens are allocated.
   */
  getDisplayRegimenClass(): string | null {
    const regimens = this.regimens();
    if (!regimens?.length) return null;

    const allocatedClasses = [...new Set(regimens.map((r: AllocatedRegimen) => r?.class).filter(Boolean))];
    if (allocatedClasses.length === 0) return null;

    const faceClass = this.appConfig.Shared.Regimen.Class.FACE;
    const hairClass = this.appConfig.Shared.Regimen.Class.HAIR;

    const hasFace = allocatedClasses.includes(faceClass);
    const hasHair = allocatedClasses.includes(hairClass);

    if (hasFace && hasHair) return faceClass;
    if (hasFace) return faceClass;
    if (hasHair) return hairClass;

    return allocatedClasses[0] ?? null;
  }

  setRegimenVariantId(variantId: string): void {
    this.variantId = variantId;
  }

  getRegimenVariantId(): string {
    return this.variantId;
  }

  getSelectedVariantIdFromRegimen(regimen: any): VariantId | null {
    if (!regimen?.variants || !regimen?.fixedPrice) {
      return null;
    }

    if (regimen.variantId) {
      return regimen.variantId as VariantId;
    }

    const selectedVariant = regimen.variants.find(
      (variant) => variant.fixedPrice === regimen.fixedPrice
    );

    return (selectedVariant?.variantId as VariantId) || null;
  }

  getBackgroungColourBasedOnVariantId(variantId: string): string {
    try {
      if (
        !variantId
        || this.variantId === VARIANT_ID.variant_1499
        || !REGIMEN_IMG_BACKGROUND_CLASS[variantId]
      ) {
        this.backgroundColor = REGIMEN_IMG_BACKGROUND_CLASS.variant_999;
      } else {
        this.backgroundColor = REGIMEN_IMG_BACKGROUND_CLASS[variantId];
      }
    } catch (err) {
      this.backgroundColor = REGIMEN_IMG_BACKGROUND_CLASS[VARIANT_ID.variant_1499];
    }
    return this.backgroundColor;
  }

  getRegimenTagBorderColorBasedOnBgColor(backgroundColor: string): LinearGradient {
    if (backgroundColor === 'tw-bg-teal-100' || !backgroundColor) {
      this.linearGradient = {
        color1: '#A2DBD0',
        color2: '#14796D',
      };
    } else if (backgroundColor === 'tw-bg-orange-100') {
      this.linearGradient = {
        color1: '#EFC9A1',
        color2: '#D65C1A',
      };
    } else if (backgroundColor === 'tw-bg-blue-100') {
      this.linearGradient = {
        color1: '#4973B4',
        color2: '#B6CCE7',
      };
    }
    return this.linearGradient;
  }

  getRegimenVariantImages(regimenClass: string): any {
    if (regimenClass === this.appConfig.Shared.Regimen.Class.FACE) {
      const regimenVariantImages = this.localStorageService.getValue('faceRegimenVariantImages');
      if (regimenVariantImages) {
        return JSON.parse(regimenVariantImages);
      }
      return null;
    } else if (regimenClass === this.appConfig.Shared.Regimen.Class.HAIR) {
      const regimenVariantImages = this.localStorageService.getValue('hairRegimenVariantImages');
      if (regimenVariantImages) {
        return JSON.parse(regimenVariantImages);
      }
      return null;
    }
    return null;
  }

  async setRegimenVariantImages(regimen: any, waitForExecution: boolean = false): Promise<void> {
    if (waitForExecution) {
      await this.updateRegimenVariantImages(regimen);
    } else {
      // Run in background without blocking
      setTimeout(() => {
        this.updateRegimenVariantImages(regimen);
      }, 0);
    }
  }

  async updateRegimenVariantImages(regimen: any): Promise<void> {
    try {
      const imageUrl = await this.get1999VariantImage(regimen);
      const regimenVariantImages = {
        variant_1499: regimen.regimenCoverImage,
        variant_1999: imageUrl,
      }
      if (regimen.class === this.appConfig.Shared.Regimen.Class.FACE) {
        this.localStorageService.setValue('faceRegimenVariantImages', JSON.stringify(regimenVariantImages));
      } else if (regimen.class === this.appConfig.Shared.Regimen.Class.HAIR) {
        this.localStorageService.setValue('hairRegimenVariantImages', JSON.stringify(regimenVariantImages));
      } else {
        this.localStorageService.setValue('regimenVariantImages', JSON.stringify(regimenVariantImages));
      }
      this._variantImagesReady.set(true);
    } catch (error) {
      const regimenVariantImages = {
        variant_1499: regimen.regimenCoverImage,
        variant_1999: regimen.regimenCoverImage,
      }
      this.localStorageService.setValue('regimenVariantImages', JSON.stringify(regimenVariantImages));
    }
  }

  async get1999VariantImage(regimen: any): Promise<string> {
    const variant1999 = regimen.variants.find((variant: any) => variant.variantId === this.appConfig.Shared.Regimen.VariantId.variant_1999);
    const productIds = extractRegimenProducts(variant1999).map((product: any) => product.product.objectId).sort().join('_');
    const regimenCoverImage = `regimenCoverPhoto_${productIds}`;
    const result = await this.connectionService.fetchImageFromMediaLink(regimenCoverImage);
    return result[0].get('link');
  }

  getEnabledPlansForMoneyBackGuarantee(moneyBackGuaranteeExperiment: Experiment | null, regimenClass: string, user: User): PlanName[] {
    const isExperimentEnabled = moneyBackGuaranteeExperiment?.enable;

    if (!isExperimentEnabled || !regimenClass || !moneyBackGuaranteeExperiment?.variant) {
      return [];
    }

    if (!this.userService.isUnpaidUser(user)) {
      return [];
    }

    const variantKeys = Object.keys(moneyBackGuaranteeExperiment?.variant || {});
    const normalizedRegimenClass = regimenClass.toLowerCase();
    const matchingRegimenClass = variantKeys.find(variantKey => variantKey.toLowerCase() === normalizedRegimenClass);

    if (!matchingRegimenClass) {
      return [];
    }

    const variantIdsOfSelectedClass = moneyBackGuaranteeExperiment.variant[matchingRegimenClass];

    if (!Array.isArray(variantIdsOfSelectedClass) || variantIdsOfSelectedClass.length === 0) {
      return [];
    }

    // Check all plans to see which ones have their variant IDs in the experiment array
    const enabledPlans: PlanName[] = [];
    Object.keys(selectedPlanAndVariantIdMap).forEach((plan) => {
      const planName = plan as PlanName;
      const variantId = selectedPlanAndVariantIdMap[planName];
      const variantIdStr = String(variantId);
      const isIncluded = variantIdsOfSelectedClass.some(v => String(v) === variantIdStr);

      if (isIncluded) {
        enabledPlans.push(planName);
      }
    });

    return enabledPlans;
  }

  private getMoneyBackGuaranteeService(): any {
    return {
      en: ['100% Money Back Guarantee', '100% refund, if no improvement seen after following the plan for given time.'],
      hi: ['100% पैसा वापस गारंटी', 'तय समय तक प्लान फॉलो करने पर भी सुधार न दिखे, तो 100% रिफंड मिलेगा।'],
      te: ['100% డబ్బులు తిరిగి ఇచ్చే హామీ', 'ఇచ్చిన కాలానికి ప్లాన్‌ను ఫాలో చేసినప్పటికీ మెరుగుదల కనిపించకపోతే, 100% రీఫండ్ అందుతుంది।'],
      kn: ['100% ಹಣ ಹಿಂದಿರುಗಿಸುವ ಗ್ಯಾರಂಟಿ', 'ನಿಗದಿತ ಸಮಯದವರೆಗೆ ಪ್ಲಾನ್ ಫಾಲೋ ಮಾಡಿದರೂ ಸುಧಾರಣೆ ಕಾಣಿಸದಿದ್ದರೆ, 100% ರಿಫಂಡ್ ದೊರೆಯುತ್ತದೆ।'],
      ta: ['100% Money Back Guarantee', '100% refund, if no improvement seen after following the plan for given time.'],
      ml: ['100% Money Back Guarantee', '100% refund, if no improvement seen after following the plan for given time.'],
      bn: ['100% Money Back Guarantee', '100% refund, if no improvement seen after following the plan for given time.'],
      mr: ['100% पैसे परत मिळण्याची हमी', 'तय समय तक प्लान फॉलो करने पर भी सुधार न दिखे, तो 100% रिफंड मिलेगा।'],
      img: '/assets/images/money_back_guarantee.png',
    };
  }

  getServicesForPlan(
    services: any[],
    plan: PlanName,
    enabledPlans: PlanName[]
  ): any[] {
    if (!services || services.length === 0) {
      return [];
    }

    // Check if the plan is enabled for money back guarantee
    const shouldShowMoneyBackGuarantee = enabledPlans?.includes(plan) ?? false;

    if (!shouldShowMoneyBackGuarantee) {
      return services;
    }

    const moneyBackGuaranteeService = this.getMoneyBackGuaranteeService();
    const result = [...services];
    result.splice(1, 0, moneyBackGuaranteeService);
    return result;
  }

  sendRegimenAllocatedEventForIOS(userAge: string): void {
    this.appBridgeService.logEventInBranchAndFirebaseFromiOS({ branch: { name: 'RegimenAllocated' }, firebase: { name: 'RegimenAllocated' } });
    const isAdult = userAge ? Number.parseInt(userAge, 10) >= 18 : false;
    if (isAdult) {
      this.appBridgeService.logEventInBranchAndFirebaseFromiOS({
        branch: { name: 'RegimenAllocated18Plus' },
        firebase: { name: 'RegimenAllocated18Plus' },
      });
    }
  }

  /**
   * Converts variant ID to plan name
   * @param variantId - The variant ID to convert
   * @returns The corresponding plan name or undefined if not found
   */
  getPlanNameByVariantId(variantId: VariantId): PlanName | undefined {
    return Object.keys(selectedPlanAndVariantIdMap).find(
      (planName) => selectedPlanAndVariantIdMap[planName as PlanName] === variantId
    ) as PlanName | undefined;
  }

  isAnyRegimenPreparationTimePending(regimens: any[]): boolean {
    return !!regimens?.some((regimen: any) => regimen.isPreparationTimePending === true);
  }

  updateRegimenPreparationState(regimens: any[]): void {
    if (!regimens?.length) {
      this._isAnyRegimenPreparationOngoing.set(false);
      return;
    }
    this._isAnyRegimenPreparationOngoing.set(this.isAnyRegimenPreparationTimePending(regimens));
  }

  setRegimenPreparationState(regimens: any[], onPreparationComplete?: (updated: any[]) => void): any[] {
    const mapped = this.mapRegimensWithPreparationState(regimens);
    this.schedulePreparationCompleteCallbacks(mapped, onPreparationComplete);
    return mapped;
  }

  private isRegimenPreparationPending(regimen: Regimen): boolean {
    if (!regimen.activeFrom) return false;
    const activeTime = new Date(regimen.activeFrom?.iso).getTime();
    const curTime = Date.now();
    return activeTime - curTime > 0;
  }

  private getPreparationDelayMs(regimen: Regimen): number {
    if (!regimen.activeFrom) return 0;
    const activeTime = new Date(regimen.activeFrom?.iso).getTime();
    const curTime = Date.now();
    return Math.max(0, activeTime - curTime);
  }

  private mapRegimensWithPreparationState(regimens: any[]): any[] {
    return regimens.map((regimen: Regimen) => ({
      ...regimen,
      isPreparationTimePending: this.isRegimenPreparationPending(regimen),
    }));
  }

  async getRegimens(include: ParseKeys<Table.Regimen>[] = REGIMEN_FETCH_INCLUDE): Promise<AllocatedRegimen[]> {
    const regimens = this.regimens();

    if (regimens.length === 0 && !this.regimensFetched()) {
      const fetchedRegimens = await this.connectionService.fetchRegimens(undefined, true, true, include);
      return Array.isArray(fetchedRegimens) ? fetchedRegimens : [];
    }

    return regimens;
  }

  async getRegimenByRegimenId(regimenId: string, include: ParseKeys<Table.Regimen>[] = REGIMEN_FETCH_INCLUDE): Promise<Regimen | null> {
    const regimens = await this.getRegimens(include);
    const selectedRegimen = regimens.find((regimen) => regimen?.regimenId === regimenId);
    return selectedRegimen || null;
  }

  updateRegimenInStore(updatedRegimen: any): void {
    const currentRegimens = this.regimens();
    const updatedRegimens = currentRegimens.map((regimen) =>
      regimen.regimenId === updatedRegimen.regimenId ? updatedRegimen : regimen,
    );
    this.store.dispatch(RegimenStateUpdate({ regimens: updatedRegimens }));
  }

  invalidateRegimenCache(): void {
    this.store.dispatch(RegimenStateUpdate({ regimens: [], regimensFetched: false }));
  }

  private schedulePreparationCompleteCallbacks(
    mapped: any[],
    onPreparationComplete?: (updated: any[]) => void,
  ): void {
    mapped.forEach((regimen: Regimen) => {
      if (!regimen.activeFrom || !regimen.isPreparationTimePending) return;
      const delay = this.getPreparationDelayMs(regimen);
      if (delay <= 0) return;
      setTimeout((): void => {
        const updated = this.setRegimenPreparationState(mapped, onPreparationComplete);
        this.updateRegimenPreparationState(updated);
        onPreparationComplete?.(updated);
      }, delay);
    });
  }

}
